import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { usePrepopulationContext } from 'domain/people/employee/prepopulation/PrepopulationContext';
import { useFormContext, DEFAULT_ERROR_MESSAGE, isEmptyFormErrors } from 'framework/form';
import ContactInfoForm from './ContactInfoForm';
import EditDialogActions from './EditDialogActions';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';

interface ContactInfoPrepopulateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedContactInfoData?: void) => void;
}

const ContactInfoPrepopulateModal: React.FC<ContactInfoPrepopulateModalProps> = ({ isOpen, onClose, onSave }) => {
  const { data } = usePrepopulationContext();
  const { data: formData, setFormData, submitForm, validateForm } = useFormContext();
  const { addMessage } = useMessageService();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && data && Object.keys(data).length > 0) {
      const prepopulatedData = {
        ...formData,
        extendedInformation: {
          ...formData?.extendedInformation,
          homeAddress: (data as any).extendedInformation?.homeAddress || formData?.extendedInformation?.homeAddress,
        },
      };
      setFormData?.(prepopulatedData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSubmit = async () => {
    try {
      setIsSaving(true);

      const errors = validateForm?.();

      if (errors && !isEmptyFormErrors(errors)) {
        setIsSaving(false);
        return;
      }

      const response = await submitForm?.();
      onSave(response);
    } catch (error) {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          overflow: 'visible',
        },
      }}
    >
      <DialogContent sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ContactInfoForm />
      </DialogContent>
      <EditDialogActions onClose={handleClose} onSave={handleSubmit} isSaving={isSaving} />
    </Dialog>
  );
};

export default ContactInfoPrepopulateModal;
